"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { CircleCheck as CheckCircle2, Calendar, ShieldCheck, ArrowRight, Clock, Sparkles, MessageSquare, Loader, UserCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const consultationBenefits = [{
  title: "Personalized SEO Strategy Review",
  description: "An in-depth review of your site's technical structure, search visibility, and organic growth opportunities."
}, {
  title: "Actionable Immediate Next Steps",
  description: "A clear, prioritized list of recommendations you can implement right away to improve search performance."
}, {
  title: "Modern AI Search Insights",
  description: "Expert guidance on optimizing for AI search experiences alongside traditional search engines."
}, {
  title: "DIY vs. Consultant Clarity",
  description: "An honest assessment of whether DIY tools can achieve your goals or if tailored consultant guidance is needed."
}];
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-muted/40 border border-primary/20 rounded-xl p-8 text-center space-y-4">
        <div className="inline-flex items-center justify-center size-14 rounded-full bg-primary/10 text-primary mx-auto">
          <CheckCircle2 className="size-8 text-primary" />
        </div>
        <h3 className="text-xl font-bold font-serif text-foreground">
          Consultation Request Received!
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">
          {successMessage}
        </p>
        <div className="pt-2">
          <Button type="button" variant="outline" onClick={resetForm} className="border-primary/30 text-foreground hover:bg-muted">
            Submit Another Request
          </Button>
        </div>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="full_name" rules={{
      required: "Please enter your full name",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Your name" className="bg-background border-input focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="Email address" className="bg-background border-input focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="Phone (optional)" className="bg-background border-input focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_type" rules={{
      required: "Please select a service interest"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Service Interest</FormLabel>
            <FormControl>
              <select className="file:text-foreground placeholder:text-muted-foreground border-input flex h-9 w-full min-w-0 rounded-md border bg-background px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-foreground" {...field}>
                <option value="">Select a service interest...</option>
                <option value="National SEO">National SEO</option>
                <option value="International SEO">International SEO</option>
                <option value="Local SEO">Local SEO</option>
                <option value="B2B SEO & Lead Generation">B2B SEO & Lead Generation</option>
                <option value="Ecommerce SEO">Ecommerce SEO</option>
                <option value="Technical SEO">Technical SEO</option>
                <option value="SEO Audits">SEO Audits</option>
                <option value="AI Search Optimisation">AI Search Optimisation</option>
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="terms" rules={{
      required: "You must agree to the privacy policy to proceed"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <div className="flex items-start gap-2 pt-1">
              <FormControl>
                <input type="checkbox" id="terms-checkbox-home" className="mt-1 size-4 rounded border-input text-primary focus:ring-ring accent-primary cursor-pointer" checked={Boolean(field.value)} onChange={e => field.onChange(e.target.checked)} />
              </FormControl>
              <FormLabel htmlFor="terms-checkbox-home" className="text-xs text-muted-foreground font-normal leading-tight cursor-pointer">
                I agree to the privacy policy and consent to Yassine Essaadi contacting me regarding my SEO consultation request.
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base py-3 shadow-md transition-all cursor-pointer mt-2">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Scheduling Session...
          </> : <>
            Book a Free SEO Consultation
            <ArrowRight className="ml-2 size-4" />
          </>}
      </Button>

      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-1">
        <span className="flex items-center gap-1">
          <ShieldCheck className="size-3.5 text-primary" />
          100% Free &amp; No Obligation
        </span>
        <span className="flex items-center gap-1">
          <Clock className="size-3.5 text-primary" />
          30-Minute Video Session
        </span>
      </div>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  return <section id="finalconversionconsultation" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column: Heading & Key Value Takeaways */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-foreground text-xs font-semibold tracking-wide uppercase">
                <Sparkles className="size-3.5 text-accent" />
                <span>Direct Expert Access</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight">
                Ready to Accelerate Your Organic Growth?
              </h2>

              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
                Book a free, 30-minute 1-on-1 SEO strategy session with me, Yassine Essaadi.
                We will review your current search positioning, analyze key growth opportunities,
                and outline actionable steps tailored directly to your business goals.
              </p>
            </div>

            {/* Benefit Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {consultationBenefits.map((benefit, index) => <div key={benefit.title} data-index={index} className="bg-card border border-border/60 rounded-lg p-4 shadow-2xs hover:shadow-sm transition-shadow space-y-1.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <h3 className="font-semibold text-foreground text-sm font-sans">
                      {benefit.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground text-xs leading-normal pl-6">
                    {benefit.description}
                  </p>
                </div>)}
            </div>

            {/* Direct Expert Note */}
            <div className="border-l-4 border-primary pl-4 py-2 bg-muted/30 rounded-r-lg space-y-1">
              <div className="flex items-center gap-2 text-foreground font-medium text-sm">
                <UserCheck className="size-4 text-primary" />
                <span>Unsure whether DIY software or a consultant is right for you?</span>
              </div>
              <p className="text-muted-foreground text-xs leading-relaxed">
                I will give you an objective view of what software can handle on its own versus when you need hands-on consultant strategy.
              </p>
            </div>
          </div>

          {/* Right Column: Lead Form Card */}
          <div className="lg:col-span-5">
            <Card className="shadow-lg border border-border bg-card rounded-2xl overflow-hidden">
              <CardHeader className="bg-primary/5 pb-4 border-b border-border/50">
                <div className="flex items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
                    <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                    <span>Free SEO Consultation</span>
                  </div>
                  <span className="text-xs font-semibold text-accent px-2.5 py-0.5 rounded-full bg-secondary">
                    30 Min Video Call
                  </span>
                </div>
                <CardTitle className="text-xl font-bold font-serif text-foreground pt-1">
                  Book Your Strategy Session
                </CardTitle>
                <CardDescription className="text-muted-foreground text-xs">
                  Fill in your details below and I will get back to you within 24 hours to confirm our meeting time.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6">
                <FormProvider className="w-full" formId="consultation_form_home" sectionName="final-conversion-consultation" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                service_type: "",
                terms: false
              }} validationRules={{
                full_name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: false
                },
                service_type: {
                  required: true
                },
                terms: {
                  required: true
                }
              }} submitText="Book a Free SEO Consultation" successMessage="Thank you for booking! I have received your details and will contact you directly within 24 hours to schedule our strategy session.">
                  <ConsultationFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </motion.div>
      </div>
    </section>;
}